/**
 * Typed-string id newtypes — the TS mirror of `backend/aletheia/common/ids.py`
 * (conventions.md §2.1).
 *
 * Every id in Aletheia is a UUID-v7 string. On the backend these are `NewType` aliases
 * over `str`; on the wire they are plain lowercase-hyphenated strings. Here we use the
 * branded-type pattern so the TS checker rejects passing a `NoteId` where an `EntityId`
 * is expected — the same guarantee the Python `NewType`s give — while the runtime value
 * stays a plain string.
 *
 * The brand is phantom (`& { readonly __brand: ... }`): it exists only at type-check
 * time. The `asXId` helpers are unchecked narrowings — they assert intent at the wire
 * boundary (`src/api/`), they do not validate UUID-v7 shape (the backend already did).
 */

declare const brand: unique symbol;
type Branded<T, B extends string> = T & { readonly [brand]: B };

export type NoteId = Branded<string, 'NoteId'>;
export type EntityId = Branded<string, 'EntityId'>;
export type ChunkId = Branded<string, 'ChunkId'>;
export type TagId = Branded<string, 'TagId'>;
export type NotebookId = Branded<string, 'NotebookId'>;
export type TopicId = Branded<string, 'TopicId'>;
export type AttachmentId = Branded<string, 'AttachmentId'>;

export const asNoteId = (s: string): NoteId => s as NoteId;
export const asEntityId = (s: string): EntityId => s as EntityId;
export const asChunkId = (s: string): ChunkId => s as ChunkId;
export const asTagId = (s: string): TagId => s as TagId;
export const asNotebookId = (s: string): NotebookId => s as NotebookId;
export const asTopicId = (s: string): TopicId => s as TopicId;
export const asAttachmentId = (s: string): AttachmentId => s as AttachmentId;
